import React, { useEffect, useState, useCallback } from "react";
import {
  Container, Row, Col, Card, CardBody, Spinner, Badge, Button,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Alert,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import {
  getPropertiesByOwner, createProperty, getAllTenants,
  onUnreadNotifications, markNotificationRead, markAllNotificationsRead,
} from "../../../helpers/firestoreService";
import { useAuthUser } from "../../../hooks/AuthProvider";

// ── Tips data ────────────────────────────────────────────────────────────────
const TIPS = [
  {
    icon: "bx bx-building-house",
    color: "#4f46e5",
    title: "Start with a Property",
    body: "Every tenant, lease, and support portal is anchored to a property. Add your first one to unlock the full Porcher workflow — tenants, leases, and tickets all in one place.",
    action: { label: "Go to Properties", to: "/properties" },
  },
  {
    icon: "bx bx-file-blank",
    color: "#34c38f",
    title: "Collect Rent and Track Applications",
    body: "Set up rent collection on a property, then review every application, payment and inspection across your portfolio from one place.",
    action: { label: "View Applications", to: "/applications" },
  },
  {
    icon: "bx bx-brain",
    color: "#f1b44c",
    title: "AI Assistance on Tenant Incidents",
    body: "When tenants submit support tickets, Porcher's AI analyzes the issue, suggests resolutions, drafts responses, and escalates when needed — so no incident falls through the cracks.",
    action: { label: "View Portals", to: "/portals" },
  },
];

const typeIcon = (type) => {
  if (!type) return { icon: "bx bx-bell", color: "#4f46e5" };
  if (type.includes("ticket")) return { icon: "bx bx-support", color: "#50a5f1" };
  if (type.includes("lease")) return { icon: "bx bx-file-blank", color: "#34c38f" };
  if (type.includes("tenant")) return { icon: "bx bx-user", color: "#f1b44c" };
  return { icon: "bx bx-bell", color: "#4f46e5" };
};

const Overview = () => {
  document.title = "Overview | Porcher";

  const user = useAuthUser();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [markingAll, setMarkingAll] = useState(false);

  // Tips slider
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const props = await getPropertiesByOwner(user.uid);
        setProperties(props);
      } catch (e) {
        console.error("Overview properties load error:", e);
      } finally {
        setLoading(false);
      }
      // Load tenants independently — failure shouldn't blank the properties panel
      try {
        const ten = await getAllTenants(user.uid);
        setTenants(ten);
      } catch (e) {
        console.error("Overview tenants load error:", e);
      }
    })();
  }, [user]);

  // Realtime notifications
  useEffect(() => {
    if (!user) return;
    const unsub = onUnreadNotifications(user.uid, setNotifications);
    return unsub;
  }, [user]);

  // Auto-advance tips every 10 s
  useEffect(() => {
    const id = setInterval(() => setTipIndex((i) => (i + 1) % TIPS.length), 10000);
    return () => clearInterval(id);
  }, []);

  const openPropertiesCreate = useCallback(() => {
    navigate("/properties?openCreate=true");
  }, [navigate]);

  const handleMarkAll = async () => {
    setMarkingAll(true);
    try { await markAllNotificationsRead(user.uid); } finally { setMarkingAll(false); }
  };

  if (loading) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  const tip = TIPS[tipIndex];

  // Derive smart system notifications
  const smartNotifs = [];
  if (properties.length === 0) {
    smartNotifs.push({
      id: "__no_property__",
      smart: true,
      icon: "bx bx-building-house",
      color: "#4f46e5",
      message: "You don't have any properties yet. Add your first property to get started.",
      action: { label: "Add Property", onClick: openPropertiesCreate },
    });
  }

  const totalNotifCount = notifications.length + smartNotifs.length;

  return (
    <div className="page-content">
      <Container fluid>

        {/* ── Hero Banner ──────────────────────────────────────────────────── */}
        <div
          className="rounded mb-4 p-4 text-white position-relative overflow-hidden"
          style={{ minHeight: 140 }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, #4f46e5 0%, #34c38f 100%)",
            }}
          />
          <div className="position-relative" style={{ zIndex: 1 }}>
            <h3 className="mb-1 text-white">
              Welcome back{user?.displayName ? `, ${user.displayName}` : ""}
            </h3>
            <p className="mb-0 opacity-75">
              You have{" "}
              <strong>{properties.length}</strong>{" "}
              {properties.length === 1 ? "property" : "properties"} and{" "}
              <strong>{tenants.length}</strong>{" "}
              {tenants.length === 1 ? "tenant" : "tenants"} in your portfolio.
            </p>
          </div>
        </div>

        {/* ── Tips Slider ──────────────────────────────────────────────────── */}
        <Card className="border-0 shadow-sm mb-4 overflow-hidden">
          <CardBody className="p-0">
            <div
              className="d-flex align-items-stretch"
              style={{ minHeight: 160 }}
            >
              <div
                style={{
                  width: 6,
                  flexShrink: 0,
                  background: tip.color,
                  transition: "background 0.4s ease",
                }}
              />
              <div className="flex-grow-1 p-4 d-flex flex-column justify-content-between">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 48,
                      height: 48,
                      background: `${tip.color}18`,
                      transition: "background 0.4s ease",
                    }}
                  >
                    <i
                      className={tip.icon}
                      style={{ fontSize: "1.5rem", color: tip.color, transition: "color 0.4s ease" }}
                    />
                  </div>
                  <div>
                    <p className="text-muted small mb-1 fw-semibold text-uppercase" style={{ letterSpacing: "0.06em" }}>
                      {tipIndex + 1} of {TIPS.length}
                    </p>
                    <h5 className="mb-1 fw-bold">{tip.title}</h5>
                    <p className="text-muted mb-0" style={{ maxWidth: 560 }}>{tip.body}</p>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <div className="d-flex gap-2">
                    {TIPS.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setTipIndex(i)}
                        style={{
                          width: i === tipIndex ? 22 : 8,
                          height: 8,
                          borderRadius: 4,
                          border: "none",
                          padding: 0,
                          background: i === tipIndex ? tip.color : "#dee2e6",
                          transition: "width 0.3s ease, background 0.3s ease",
                          cursor: "pointer",
                        }}
                      />
                    ))}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => setTipIndex((i) => (i - 1 + TIPS.length) % TIPS.length)}
                    >
                      <i className="bx bx-chevron-left" />
                    </button>
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => setTipIndex((i) => (i + 1) % TIPS.length)}
                    >
                      <i className="bx bx-chevron-right" />
                    </button>
                    <Link
                      to={tip.action.to}
                      className="btn btn-sm"
                      style={{ background: tip.color, color: "#fff", border: "none" }}
                    >
                      {tip.action.label} <i className="bx bx-right-arrow-alt ms-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* ── Properties + Notifications ───────────────────────────────────── */}
        <Row className="g-4">

          {/* Properties column */}
          <Col lg={8}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-0">
                      <i className="bx bx-building-house text-primary me-2" />
                      Properties
                      <Badge color="primary" pill className="ms-2">{properties.length}</Badge>
                    </h5>
                    <small className="text-muted">Select a property to manage tenants, leases and tickets.</small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button color="primary" size="sm" onClick={openPropertiesCreate}>
                      <i className="bx bx-plus me-1" /> Add Property
                    </Button>
                    {properties.length > 0 && (
                      <Link to="/properties" className="btn btn-sm btn-outline-secondary">
                        View All
                      </Link>
                    )}
                  </div>
                </div>

                {properties.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="bx bx-building-house d-block mb-2" style={{ fontSize: "2.5rem", opacity: 0.3 }} />
                    <p className="mb-0 small">No properties yet — check the notification panel to get started.</p>
                  </div>
                ) : (
                  <Row className="g-3">
                    {properties.map((p) => {
                      const tenantCount = tenants.filter((t) => t.propertyId === p.id).length;
                      return (
                        <Col md={6} key={p.id}>
                          <Link to={`/properties/${p.id}`} className="text-decoration-none">
                            <div
                              className="rounded p-3 d-flex align-items-center gap-3"
                              style={{
                                border: "1px solid #e9ecef",
                                transition: "box-shadow 0.15s ease, border-color 0.15s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = "0 4px 16px rgba(79,70,229,0.12)";
                                e.currentTarget.style.borderColor = "#4f46e5";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = "";
                                e.currentTarget.style.borderColor = "#e9ecef";
                              }}
                            >
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 44, height: 44, background: "#4f46e518" }}
                              >
                                <i className="bx bx-building-house text-primary" style={{ fontSize: "1.3rem" }} />
                              </div>
                              <div className="overflow-hidden flex-grow-1">
                                <p className="mb-0 fw-semibold text-dark text-truncate">{p.address}</p>
                                <small className="text-muted">
                                  {tenantCount === 0 ? "No tenants yet" : `${tenantCount} tenant${tenantCount !== 1 ? "s" : ""}`}
                                </small>
                              </div>
                              <i className="bx bx-chevron-right text-muted flex-shrink-0" />
                            </div>
                          </Link>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Notifications column */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="d-flex flex-column" style={{ minHeight: 240 }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    <i className="bx bx-bell text-warning me-2" />
                    Notifications
                    {totalNotifCount > 0 && (
                      <Badge color="danger" pill className="ms-2">{totalNotifCount}</Badge>
                    )}
                  </h5>
                  {notifications.length > 0 && (
                    <button
                      className="btn btn-sm btn-link text-muted p-0"
                      onClick={handleMarkAll}
                      disabled={markingAll}
                    >
                      {markingAll ? <Spinner size="sm" /> : "Mark all read"}
                    </button>
                  )}
                </div>

                {totalNotifCount === 0 ? (
                  <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-muted py-3">
                    <i className="bx bx-check-circle d-block mb-2" style={{ fontSize: "2rem", opacity: 0.35 }} />
                    <p className="mb-0 small">You&apos;re all caught up!</p>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2 flex-grow-1">
                    {/* Smart system notifications first */}
                    {smartNotifs.map((n) => (
                      <div
                        key={n.id}
                        className="rounded p-3 d-flex align-items-start gap-2"
                        style={{ background: `${n.color}10`, border: `1px solid ${n.color}30` }}
                      >
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mt-1"
                          style={{ width: 30, height: 30, background: `${n.color}20` }}
                        >
                          <i className={n.icon} style={{ fontSize: "0.95rem", color: n.color }} />
                        </div>
                        <div className="flex-grow-1">
                          <p className="mb-1 small">{n.message}</p>
                          {n.action && (
                            <button
                              className="btn btn-sm p-0 fw-semibold"
                              style={{ color: n.color, fontSize: "0.8rem" }}
                              onClick={n.action.onClick}
                            >
                              {n.action.label} →
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Firestore notifications */}
                    {notifications.map((n) => {
                      const { icon, color } = typeIcon(n.type);
                      return (
                        <div
                          key={n.id}
                          className="rounded p-3 d-flex align-items-start gap-2"
                          style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}
                        >
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mt-1"
                            style={{ width: 30, height: 30, background: `${color}18` }}
                          >
                            <i className={icon} style={{ fontSize: "0.95rem", color }} />
                          </div>
                          <div className="flex-grow-1 overflow-hidden">
                            <p className="mb-0 small text-dark" style={{ lineHeight: 1.4 }}>{n.message}</p>
                            {n.createdAt && (
                              <small className="text-muted" style={{ fontSize: "0.72rem" }}>
                                {n.createdAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{" "}
                                · {n.createdAt.toLocaleDateString()}
                              </small>
                            )}
                          </div>
                          <button
                            className="btn btn-sm p-0 text-muted flex-shrink-0"
                            title="Dismiss"
                            onClick={() => markNotificationRead(n.id)}
                          >
                            <i className="bx bx-x" style={{ fontSize: "1.1rem" }} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

        </Row>



      </Container>
    </div>
  );
};


export default Overview;
